import { Navigate } from 'react-router-dom';
import { Check, Tag } from 'lucide-react';
import { CheckoutButton, usePlans } from '@clerk/clerk-react/experimental';
import { Card, CardContent, CardHeader } from '../../ui/Card';
import Button from '../../ui/Button';
import { usePricingV2 } from '../../../hooks/usePricingV2';
import { PLAN_CATALOG, introPricing, planFromFeatures, planSummary } from '../../../billing/pricingV2';

const planCheckoutIds = (clerkPlans) =>
  Object.fromEntries((clerkPlans ?? []).map(({ slug, id }) => [slug, id]));

export function PlanOffer({ plan, current, checkoutPlanId }) {
  const { listPriceUsd, priceUsd, promo } = introPricing(plan);
  const { features } = planSummary(planFromFeatures(plan.features, plan.slug));

  return (
    <Card variant="elevated" data-testid={`pricing-plan-${plan.slug}`}>
      <CardHeader>
        <h2 className="text-heading font-semibold text-gray-900 dark:text-gray-100">{plan.name}</h2>
        <p className="mt-2 flex items-baseline gap-2">
          {promo && (
            <s data-testid="pricing-list-price" className="text-body text-gray-500 dark:text-gray-400">
              ${listPriceUsd}
            </s>
          )}
          <span data-testid="pricing-price" className="text-heading-xl font-bold text-gray-900 dark:text-gray-100">
            ${priceUsd}
          </span>
          <span className="text-body-sm text-gray-600 dark:text-gray-400">/mo</span>
        </p>
        {promo && (
          <p data-testid="pricing-promo" className="mt-2 flex items-start gap-1.5 text-body-sm text-amber-800 dark:text-amber-300">
            <Tag className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>
              <span className="font-medium">{promo.note}</span>
              <span className="block text-caption">{promo.terms}</span>
            </span>
          </p>
        )}
      </CardHeader>
      <CardContent>
        <ul className="space-y-1.5 text-body-sm text-gray-600 dark:text-gray-400 mb-4">
          {features.map(feature => (
            <li key={feature} className="flex items-start gap-2">
              <Check className="w-4 h-4 mt-0.5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
              {feature}
            </li>
          ))}
        </ul>
        {current ? (
          <Button variant="secondary" className="w-full" disabled>Current plan</Button>
        ) : checkoutPlanId ? (
          <CheckoutButton
            planId={checkoutPlanId}
            planPeriod="month"
            for="organization"
            newSubscriptionRedirectUrl="/settings"
          >
            <Button variant="primary" className="w-full" data-testid={`pricing-checkout-${plan.slug}`}>
              Choose {plan.name}
            </Button>
          </CheckoutButton>
        ) : (
          <Button variant="secondary" className="w-full" disabled data-testid={`pricing-checkout-unavailable-${plan.slug}`}>
            Checkout not available yet
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function PricingPlans({ currentSlug }) {
  const { data: clerkPlans } = usePlans({ for: 'organization', pageSize: 10 });
  const checkoutIds = planCheckoutIds(clerkPlans);

  return (
    <div className="grid gap-4 md:grid-cols-3">
      {PLAN_CATALOG.map(plan => (
        <PlanOffer
          key={plan.slug}
          plan={plan}
          current={plan.slug === currentSlug}
          checkoutPlanId={checkoutIds[plan.slug] ?? null}
        />
      ))}
    </div>
  );
}

export default function PricingPage() {
  const { enabled, plan } = usePricingV2();
  if (!enabled) return <Navigate to="/dashboard" replace />;

  return (
    <div className="max-w-5xl mx-auto" data-testid="pricing-page">
      <h1 className="text-heading-xl font-bold text-gray-900 dark:text-gray-100">Plans</h1>
      <p className="mt-1 mb-6 text-body text-gray-600 dark:text-gray-400">
        Billed monthly to your organization. Only organization admins can change the plan.
      </p>
      <PricingPlans currentSlug={plan.slug} />
    </div>
  );
}
